import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'

const logoUrl = '/assets/logo.svg'

const products = [
  {
    label: 'Kinerja',
    subtitle: 'Performance Management',
    href: '/kinerja-product',
  },
  {
    label: 'Wawasan',
    subtitle: 'Talent Intelligence',
    href: '/wawasan-product',
  },
  {
    label: 'Rekrut',
    subtitle: 'Smart Recruitment',
    href: '/rekrut-product',
  },
]

const navLinks = [
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Pricing', href: '/#pricing' },
]

export function LandingHeader() {
  const { isAuthenticated, isLoading } = useAuth()
  const [productsOpen, setProductsOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProductsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <header className="bg-white border-b border-[#e8f5f5] sticky top-0 z-50">
      <div className="flex items-center justify-between px-6 py-4 w-full max-w-screen-xl mx-auto md:px-10 lg:px-[120px]">
        {/* Logo */}
        <a href="/" className="h-8 w-[137px] flex-shrink-0">
          <img src={logoUrl} alt="Pelajari" className="h-full w-full object-contain" />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {/* Products dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setProductsOpen((v) => !v)}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-[#000f0f] capitalize hover:text-[#359d9e] transition-colors md:text-base"
            >
              Products
              <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
            </button>

            {productsOpen && (
              <div className="absolute top-full left-0 mt-2 w-[240px] bg-white border border-[#d5f2ef] rounded-xl shadow-lg py-2 z-50">
                {products.map((p) => (
                  <a
                    key={p.href}
                    href={p.href}
                    onClick={() => setProductsOpen(false)}
                    className="flex flex-col px-4 py-3 hover:bg-[#f2fbfa] transition-colors"
                  >
                    <span className="text-sm font-semibold text-[#000f0f]">{p.label}</span>
                    <span className="text-xs text-[#359d9e]">{p.subtitle}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {navLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="px-4 py-2 text-sm font-medium text-[#000f0f] capitalize hover:text-[#359d9e] transition-colors md:text-base"
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center gap-3 md:gap-4">
          {!isLoading && (
            isAuthenticated ? (
              <a
                href="/dashboard"
                className="bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-3 py-2 text-sm font-medium capitalize transition-colors md:px-4 md:text-base"
              >
                Go to Dashboard
              </a>
            ) : (
              <>
                <a
                  href="/login"
                  className="border border-[#359d9e] text-[#359d9e] hover:bg-[#359d9e] hover:text-white rounded px-3 py-2 text-sm font-medium capitalize transition-colors md:px-4 md:text-base"
                >
                  Login
                </a>
                <a
                  href="/#book-demo"
                  className="bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-3 py-2 text-sm font-medium capitalize transition-colors md:px-4 md:text-base"
                >
                  Book a Demo
                </a>
              </>
            )
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="md:hidden p-2 text-[#000f0f] hover:text-[#359d9e]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#e8f5f5] bg-white px-6 py-4 space-y-1">
          <p className="text-xs font-semibold text-[#b3b7b7] uppercase tracking-wider px-3 py-2">Products</p>
          {products.map((p) => (
            <a
              key={p.href}
              href={p.href}
              onClick={() => setMobileOpen(false)}
              className="flex flex-col px-3 py-2 rounded-lg hover:bg-[#f2fbfa] transition-colors"
            >
              <span className="text-sm font-semibold text-[#000f0f]">{p.label}</span>
              <span className="text-xs text-[#359d9e]">{p.subtitle}</span>
            </a>
          ))}
          <div className="border-t border-[#e8f5f5] pt-2 mt-2 space-y-1">
            {navLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-[#000f0f] hover:bg-[#f2fbfa] hover:text-[#359d9e] transition-colors"
              >
                {item.label}
              </a>
            ))}
          </div>
          {!isLoading && (
            <div className="border-t border-[#e8f5f5] pt-3 mt-2 flex flex-col gap-2">
              {isAuthenticated ? (
                <a
                  href="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-center bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-4 py-2 text-sm font-medium transition-colors"
                >
                  Go to Dashboard
                </a>
              ) : (
                <>
                  <a
                    href="/login"
                    className="w-full text-center border border-[#359d9e] text-[#359d9e] hover:bg-[#359d9e] hover:text-white rounded px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Login
                  </a>
                  <a
                    href="/#book-demo"
                    className="w-full text-center bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Book a Demo
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  )
}
